import React, { useEffect, useRef, useContext, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Icon } from "@iconify/react";
import { store } from "../../../../Store";
import { usePublicHeaderDropdown } from "../../../../../hooks/header/usePublicHeaderDropdown";
import { FocusScope } from "react-aria";
import { HeaderQuickActions } from "../../../components/HeaderQuickActions";
import { MenuItem } from "../../../components/MenuItem";
import { SubMenu } from "../../../components/SubMenu";
import { buildMakeMakeComercialUrlByEnv } from "../../../../../util/host-utilities";

/**
 * Componente que devuelve el dropdown con las opciones en modo responsive. 
 * NO LOGUEADO (blog, comunidad, notificaciones, explorar, ajustes y contacto)
 * @param {object} textosInterfaz - contiene los textos de interfaz
 * @returns {JSX.Element} DropdownResponsive
 */
export const DropdownResponsive = ({ textosInterfaz }) => {
    const location = useLocation();

    const globalState = useContext(store);

    const showADropdown = usePublicHeaderDropdown();
    const visibleDropdown = showADropdown?.state?.visible;

    const triggerButton = globalState?.state?.publicHeaderDropdown?.triggerButtonRef;
    const ref = useRef(null);

    const [resourcesOptions, setResourcesOptions] = useState(false);
    const [plansOptions, setPlansOptions] = useState(false);
    const collectionUrl = buildMakeMakeComercialUrlByEnv({ path: "/libros" });

    /**Función que permite cerrar el dropdown dando click fuera de él
    * @param {event} event 
    */
    const handleOutsideClick = (event) => {
        if (triggerButton?.current?.contains(event.target)) return;
        if (ref.current && !ref.current.contains(event.target)) {
            showADropdown.setVisible(false);
        }
    };

    useEffect(() => {
        document.addEventListener('click', handleOutsideClick);

        return () => {
            document.removeEventListener('click', handleOutsideClick);
        };
    }, [triggerButton]);

    /*cierra el dropdown al hacer scroll al inicio de la página */
    useEffect(() => {
        window.addEventListener("scroll", () => {
            if (window.scrollY >= 1 && window.scrollY < 10) {
                showADropdown.setVisible(false);
            }
        });
    }, [visibleDropdown]);

    useEffect(() => {
        if (!visibleDropdown) {
            setResourcesOptions(false);
            setPlansOptions(false);
        }
    }, [visibleDropdown]);

    const closeMenu = () => {
        showADropdown.setVisible(false);
        setResourcesOptions(false);
        setPlansOptions(false);
    };

    const handleKeyDown = (e) => {
        if (e.key === "Escape") {
            closeMenu();
        }
    };

    const MENU_ITEM_BASE_CLASS =
        "link-principal d-flex justify-content-center align-items-center focusable-red";

    const plansItems = [
        {
            id: "bibliotecas",
            link: buildMakeMakeComercialUrlByEnv({
                path: "/planes/bibliotecas",
            }),
            name: textosInterfaz?.plan_bibliotecas ?? 'Plan bibliotecas',
            external: true,
            target: "_self",
        },
        {
            id: "colegios",
            link: buildMakeMakeComercialUrlByEnv({
                path: "/planes/colegios",
            }),
            name: textosInterfaz?.plan_colegios ?? "Plan colegios",
            external: true,
            target: "_self",
        }
    ];

    const resourceItems = [
        {
            id: "resources",
            link: "/comunicacion",
            name: textosInterfaz?.comunicacion ?? "Comunicación",
        },
        // {
        //     id: "pedagogical",
        //     link: "/centro-pedagogico",
        //     name: textosInterfaz?.centro_pedagogico ?? "Centro pedagógico",
        // },
        {
            id: "blog",
            link: "/blog",
            name: textosInterfaz?.blog ?? "Blog",
        },
        {
            id: "help",
            link: "/ayuda-sin-login",
            name: textosInterfaz?.ayuda ?? "Ayuda",
        },
        {
            id: "apps",
            link: "https://makemake.app/home?platform=macos",
            name: "Apps",
            external: true,
        },
    ];

    return (
        <div
            className={`dropdownMenuResponsive ${visibleDropdown ? 'activeMode' : 'inactiveMode'} `}
            role="menu"
            aria-label={textosInterfaz?.menuResponsive_label || "Menú principal"}
            onKeyDown={handleKeyDown}
        >
            <FocusScope contain restoreFocus autoFocus >
                <ul ref={ref} aria-label={textosInterfaz?.menuResponsive_list || "Opciones del menú"}>
                    {location.pathname !== '/institucional/formulario' ?
                        <>
                            <li className={`${visibleDropdown ? 'activeMode' : 'inactiveMode'}`} role="none">
                                <a
                                    href={collectionUrl}
                                    onClick={closeMenu}
                                    className={`${MENU_ITEM_BASE_CLASS}`}
                                    role="menuitem"
                                >
                                {textosInterfaz?.coleccion || 'Conoce la colección'}
                                </a>
                            </li>
                        </>
                        :

                        <li className={`${visibleDropdown ? 'activeMode' : 'inactiveMode'}`}>
                            <MenuItem
                                visible={visibleDropdown}
                                to="/"
                                onClick={closeMenu}
                                className={`${MENU_ITEM_BASE_CLASS}`}
                            >
                                {textosInterfaz?.explorar || 'Explorar'}
                            </MenuItem>
                        </li>
                    }
                    <div role="separator" className={`divider ${visibleDropdown ? 'activeMode' : 'inactiveMode'}`} ></div>

                    {/* Planes */}
                    <SubMenu
                        visible={visibleDropdown}
                        open={plansOptions}
                        onToggle={() => {
                            setPlansOptions((prev) => !prev)
                            setResourcesOptions(false)
                        }}
                        label={
                            textosInterfaz?.quiero_make || 'Planes'
                        }
                        items={plansItems}
                        onItemClick={closeMenu}
                        idPrefix="planes"
                    />

                    <div role="separator" className={`divider ${visibleDropdown ? 'activeMode' : 'inactiveMode'}`} ></div>

                    {/* Comunidad */}
                    <MenuItem
                        visible={visibleDropdown}
                        to="/comunidad"
                        onClick={closeMenu}
                        className={`${MENU_ITEM_BASE_CLASS}`}
                    >
                        {textosInterfaz?.comunidad || "Comunidad"}
                    </MenuItem>

                    <div role="separator" className={`divider ${visibleDropdown ? 'activeMode' : 'inactiveMode'}`} ></div>

                    {/* Recursos */}
                    <SubMenu
                        visible={visibleDropdown}
                        open={resourcesOptions}
                        onToggle={() => {
                            setResourcesOptions((prev) => !prev)
                            setPlansOptions(false)
                        }}
                        label={
                            textosInterfaz?.recursos2 ||
                            "Recursos"
                        }
                        items={resourceItems}
                        onItemClick={closeMenu}
                        idPrefix="recursos"
                    />
                    <div role="separator" className={`divider ${visibleDropdown ? 'activeMode' : 'inactiveMode'}`} ></div>
                    <li className={`${visibleDropdown ? 'activeMode' : 'inactiveMode'}`}>
                        <HeaderQuickActions 
                            textosInterfaz={textosInterfaz}
                            cerrarVentana={() => showADropdown.setVisible(false)}
                        />
                    </li>
                    <li className={`${visibleDropdown ? 'activeMode' : 'inactiveMode'} d-flex justify-content-center`}>
                        <Link
                            role="menuitem"
                            to="/login"
                            className={`focusable-red buttonv2 button-violet-outline py-2`}
                            onClick={() => showADropdown.setVisible(false)}
                        >
                            <Icon icon="wordpress:login" className="onsecondary--color" width={30} />
                            <span className="ml-1">{textosInterfaz?.ingresar || 'Ingresar'}</span>
                        </Link>
                    </li>
                    <li className={`close-options ${visibleDropdown ? 'activeMode' : 'inactiveMode'}`}>
                        <button role="menuitem" onClick={() => showADropdown.setVisible(false)} aria-label={textosInterfaz?.button_close_list_alt || "Cerrar la lista desplegable"} className="w-100 focusable-red ">
                            {textosInterfaz?.button_close_list || "Cerrar"}
                        </button>
                    </li>
                </ul>
            </FocusScope>
        </div>

    )
}
